import React, { useState, useMemo } from 'react';
import { usePositions } from '@/hooks/useDashboardQueries';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { PnlValue } from '@/components/common/PnlValue';
import { PositionRangeBar } from '@/components/common/PositionRangeBar';
import { DataTable, Column } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { TableSkeleton, MetricCardSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { formatINR, formatTimestamp, formatHoldingDuration } from '@/lib/utils';
import { Position } from '@/types/trading';
import {
  Layers,
  Search,
  Wallet,
  TrendingUp,
  Clock,
} from 'lucide-react';

export const PositionsView: React.FC = () => {
  const {
    data: positionsData,
    isLoading,
    isError,
    error,
    refetch,
  } = usePositions();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExchange, setSelectedExchange] = useState<string>('ALL');

  const positions = useMemo(() => positionsData?.positions || [], [positionsData]);

  // Filter positions
  const filteredPositions = useMemo(() => {
    return positions.filter((pos) => {
      const matchesSearch =
        pos.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pos.exchange.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesExchange =
        selectedExchange === 'ALL' || pos.exchange.toUpperCase() === selectedExchange;
      return matchesSearch && matchesExchange;
    });
  }, [positions, searchTerm, selectedExchange]);

  // Aggregate Metrics
  const totalUnrealizedPnl = useMemo(() => {
    return positions.reduce((acc, pos) => {
      return pos.unrealized_pnl !== null ? acc + pos.unrealized_pnl : acc;
    }, 0);
  }, [positions]);

  const totalCapitalInvested = useMemo(() => {
    return positions.reduce((acc, pos) => {
      return acc + (pos.entry_price * pos.quantity);
    }, 0);
  }, [positions]);

  const positionsWithPendingCycle = useMemo(() => {
    return positions.filter((pos) => pos.ltp === null).length;
  }, [positions]);

  // Table Columns Definition
  const columns: Column<Position>[] = [
    {
      key: 'symbol',
      header: 'Symbol & Side',
      sortable: true,
      render: (pos) => (
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-100 font-mono text-sm">
              {pos.symbol}
            </span>
            <span className="px-1.5 py-0.2 rounded bg-slate-800 border border-slate-700 text-[10px] font-mono text-slate-400">
              {pos.exchange}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] font-mono">
            <span className="px-1 py-0.2 rounded bg-emerald-950/60 text-emerald-400 border border-emerald-800/50 font-semibold">
              LONG
            </span>
            <span className="text-slate-400 font-medium">
              {pos.status || 'OPEN'}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty / Value',
      sortable: true,
      align: 'right',
      render: (pos) => {
        const positionValue = (pos.quantity || 0) * (pos.entry_price || 0);
        return (
          <div className="text-right font-mono">
            <span className="text-slate-200 font-semibold tabular-nums block">
              {pos.quantity}
            </span>
            <span className="text-slate-400 text-[11px] tabular-nums block">
              {formatINR(positionValue)}
            </span>
          </div>
        );
      },
    },
    {
      key: 'entry_price',
      header: 'Entry Price',
      sortable: true,
      align: 'right',
      render: (pos) => (
        <span className="font-mono text-slate-300 tabular-nums">
          {formatINR(pos.entry_price)}
        </span>
      ),
    },
    {
      key: 'ltp',
      header: 'LTP (Current)',
      sortable: true,
      align: 'right',
      render: (pos) => (
        <div className="text-right">
          {pos.ltp !== null && pos.ltp !== undefined ? (
            <span className="font-mono font-semibold text-slate-100 tabular-nums">
              {formatINR(pos.ltp)}
            </span>
          ) : (
            <span className="text-[10px] font-mono text-amber-400/90 bg-amber-950/40 border border-amber-900/50 px-2 py-0.5 rounded inline-block">
              Pending Cycle
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'unrealized_pnl',
      header: 'Unrealized P&L',
      sortable: true,
      align: 'right',
      render: (pos) => (
        <PnlValue
          value={pos.unrealized_pnl}
          percent={pos.unrealized_pnl_percent}
          showSign={true}
          showPercent={true}
          layout="stacked"
          align="right"
          nullPlaceholder="Pending Cycle"
        />
      ),
    },
    {
      key: 'range',
      header: 'SL / TP Target Range',
      align: 'center',
      render: (pos) => (
        <div className="min-w-[220px] max-w-[280px] py-1">
          <PositionRangeBar
            entryPrice={pos.entry_price}
            ltp={pos.ltp}
            stopLoss={pos.stop_loss}
            takeProfit={pos.take_profit}
            compact
          />
        </div>
      ),
    },
    {
      key: 'holding_duration',
      header: 'Duration',
      sortable: true,
      render: (pos) => (
        <span className="font-mono text-slate-400 text-xs flex items-center gap-1.5 whitespace-nowrap">
          <Clock className="w-3 h-3 text-slate-500 shrink-0" />
          {formatHoldingDuration(pos.holding_duration)}
        </span>
      ),
    },
  ];

  if (isLoading && positions.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Open Positions"
          icon={Layers}
        />
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
        <TableSkeleton rows={5} cols={7} />
      </div>
    );
  }

  if (isError && positions.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Open Positions"
          icon={Layers}
        />
        <ErrorState
          title="Failed to load open positions"
          message={error instanceof Error ? error.message : 'API connection error'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="animate-stagger-1">
        <PageHeader
          title="Open Positions"
          description="Active swing positions monitored with automated trailing stops, risk guards, and real-time LTP tracking."
          icon={Layers}
          badge={
            <span className="text-xs font-mono px-2.5 py-0.5 rounded-full bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 font-semibold">
              {positions.length} ACTIVE POSITIONS
            </span>
          }
        />
      </div>

      {/* Metric Cards Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-2">
        <MetricCard
          title="Unrealized P&L"
          value={
            <PnlValue
              value={totalUnrealizedPnl}
              size="xl"
              showSign={true}
            />
          }
          subtitle="Open position floating return"
          variant={totalUnrealizedPnl > 0 ? 'emerald' : totalUnrealizedPnl < 0 ? 'crimson' : 'default'}
          icon={TrendingUp}
        />

        <MetricCard
          title="Active Exposure"
          value={formatINR(totalCapitalInvested)}
          subtitle="Total capital deployed in open positions"
          variant="default"
          icon={Wallet}
        />

        <MetricCard
          title="Position Capacity"
          value={`${positions.length} / ${positionsData?.max_positions || 5}`}
          subtitle={`${positionsData?.available_capacity ?? 0} available risk slots`}
          variant="cyan"
          icon={Layers}
        />

        <MetricCard
          title="LTP Quote Sync"
          value={
            positionsWithPendingCycle > 0 ? (
              <span className="text-amber-400 text-lg">
                {positionsWithPendingCycle} Pending Cycle
              </span>
            ) : (
              <span className="text-emerald-400 text-lg">Synced</span>
            )
          }
          subtitle="LTP quote cycle sync"
          variant={positionsWithPendingCycle > 0 ? 'amber' : 'emerald'}
          icon={Clock}
        />
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-2.5 rounded-lg border border-slate-800 bg-slate-900/40 animate-stagger-3">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search symbol or exchange..."
              className="w-full pl-9 pr-3 py-1.5 text-xs font-mono bg-slate-950/80 border border-slate-800 rounded-md text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all duration-150"
            />
          </div>

          <div className="flex items-center gap-1 text-xs font-mono">
            {['ALL', 'NSE', 'BSE'].map((ex) => (
              <button
                key={ex}
                onClick={() => setSelectedExchange(ex)}
                className={`px-2.5 py-1 rounded text-[11px] font-mono transition-all duration-150 active:scale-95 ${
                  selectedExchange === ex
                    ? 'bg-cyan-950/80 text-cyan-300 border border-cyan-800/70 font-semibold'
                    : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-slate-200'
                }`}
              >
                {ex}
              </button>
            ))}
          </div>
        </div>

        <div className="text-xs font-mono text-slate-400">
          Showing {filteredPositions.length} of {positions.length} active positions
        </div>
      </div>

      {/* Desktop Dense Table */}
      <div className="hidden md:block animate-stagger-4">
        <DataTable
          data={filteredPositions}
          columns={columns}
          keyExtractor={(pos) => pos.id}
          pageSize={10}
          emptyTitle="No open positions match filter"
          emptyDescription="Try clearing your search query or waiting for the bot to open new swing setups."
        />
      </div>

      {/* Mobile / Tablet Responsive Cards Layout */}
      <div className="md:hidden space-y-3 animate-stagger-4">
        {filteredPositions.length === 0 ? (
          <EmptyState
            title="No open positions"
            description="No open positions. The bot is waiting for the next eligible trading signal."
          />
        ) : (
          filteredPositions.map((pos) => {
            const positionValue = (pos.quantity || 0) * (pos.entry_price || 0);
            return (
              <div
                key={pos.id}
                className="p-4 rounded-lg border border-slate-800 bg-slate-900/60 space-y-3.5 interactive-card transition-all duration-150 shadow-sm"
              >
                {/* Header: Symbol & Status on Left, Stacked P&L on Right */}
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-100 font-mono text-base">
                        {pos.symbol}
                      </span>
                      <span className="px-1.5 py-0.2 rounded bg-slate-800 text-[10px] font-mono text-slate-400">
                        {pos.exchange}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] font-mono">
                      <span className="px-1 py-0.2 rounded bg-emerald-950/60 text-emerald-400 border border-emerald-800/50 font-semibold">
                        LONG
                      </span>
                      <span className="text-slate-400 font-medium">
                        {pos.status || 'OPEN'}
                      </span>
                    </div>
                  </div>

                  <div className="text-right">
                    <span className="text-[10px] text-slate-500 block font-mono">UNREALIZED P&L</span>
                    <PnlValue
                      value={pos.unrealized_pnl}
                      percent={pos.unrealized_pnl_percent}
                      showSign={true}
                      showPercent={true}
                      layout="stacked"
                      align="right"
                      nullPlaceholder="Pending Cycle"
                      size="md"
                    />
                  </div>
                </div>

                {/* Row 2: Qty, Holding, Value, LTP in a clean 2x2 grid */}
                <div className="grid grid-cols-2 gap-2 text-xs font-mono pt-2 border-t border-slate-800/60">
                  <div>
                    <span className="text-[10px] text-slate-500 block">QUANTITY</span>
                    <span className="text-slate-200 font-semibold tabular-nums">{pos.quantity}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-slate-500 block">HOLDING TIME</span>
                    <span className="text-slate-300 font-medium tabular-nums">
                      {formatHoldingDuration(pos.holding_duration)}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 block">VALUE</span>
                    <span className="text-slate-200 font-semibold tabular-nums">{formatINR(positionValue)}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-slate-500 block">CURRENT LTP</span>
                    {pos.ltp !== null && pos.ltp !== undefined ? (
                      <span className="text-slate-100 font-semibold tabular-nums">{formatINR(pos.ltp)}</span>
                    ) : (
                      <span className="text-[10px] text-amber-400/90 bg-amber-950/40 px-1.5 py-0.5 rounded border border-amber-900/50">
                        Pending Cycle
                      </span>
                    )}
                  </div>
                </div>

                {/* Row 3: Entry Price Info */}
                <div className="flex items-center justify-between text-xs font-mono px-2 py-1 rounded bg-slate-950/40 border border-slate-800/40 text-slate-400">
                  <span className="text-[10px] text-slate-500">ENTRY PRICE:</span>
                  <span className="text-slate-200 tabular-nums">{formatINR(pos.entry_price)}</span>
                </div>

                {/* Row 4: Visual SL -> Entry -> LTP -> TP Range Bar */}
                <div className="pt-2 border-t border-slate-800/60 bg-slate-950/40 p-2.5 rounded-lg">
                  <PositionRangeBar
                    entryPrice={pos.entry_price}
                    ltp={pos.ltp}
                    stopLoss={pos.stop_loss}
                    takeProfit={pos.take_profit}
                  />
                </div>

                {/* Footer: Opened At */}
                <div className="flex items-center justify-between text-[11px] font-mono text-slate-500 pt-0.5">
                  <span className="text-slate-500">Opened:</span>
                  <span className="text-slate-400">{formatTimestamp(pos.opened_at, 'compact')}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
